import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BankHistoryChange,
  useGetBankDetailsHistoryQuery,
} from '../services/bankApi';
import { AggregatorOption, BankRow } from '../types/bankdetails';

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  return String(value);
};

const friendlyFieldName = (field: string): string => {
  switch (field) {
    case 'isActive':
      return 'Active';
    case 'bankId':
      return 'Bank ID';
    case 'bankid_corporate':
      return 'Corp Bank ID';
    case 'bankName':
      return 'Bank Name';
    case 'ifsc':
      return 'IFSC';
    case 'gateway':
      return 'Gateway';
    case 'trtype':
      return 'Tr Type';
    case 'paymode':
      return 'Pay Mode';
    case 'settlementDays':
      return 'Settlement Days';
    default:
      return field;
  }
};

const ChangesCell = ({ changes }: { changes?: BankHistoryChange[] }) => {
  if (!changes || changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {changes.map((c, i) => (
        <Chip
          key={`${c.field}-${i}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.72rem', height: 22 }}
          label={
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              <strong>{friendlyFieldName(c.field)}</strong>
              {`: ${formatChangeValue(c.from)} → ${formatChangeValue(c.to)}`}
            </Box>
          }
        />
      ))}
    </Stack>
  );
};

interface BankHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  selectedBank: BankRow | null;
  aggregator: AggregatorOption;
}

export function BankHistoryDialog({
  open,
  onClose,
  selectedBank,
  aggregator,
}: BankHistoryDialogProps) {
  const {
    data: historyData,
    isLoading,
    error,
  } = useGetBankDetailsHistoryQuery(
    {
      ifsc: selectedBank?.ifscCode,
      gateway: aggregator,
    },
    {
      skip: !selectedBank,
    }
  );

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bank Details History</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography>Loading history...</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bank Details History</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography color="error">Failed to load history</Typography>
            <Typography variant="body2" color="text.secondary">
              Error: {JSON.stringify(error)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const historyItems = historyData?.data?.data || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Bank Details History</DialogTitle>
      <DialogContent>
        {historyItems.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography>No history found for this bank</Typography>
            <Typography variant="body2" color="text.secondary">
              IFSC: {selectedBank?.ifscCode}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 140 }}>Date</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Type</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>Changes</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Updated By</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((item, index) => {
                    const when = item.eventDate || item.updatedDate;
                    const transactionLabel = [item.transactionType, item.paymode]
                      .filter(Boolean)
                      .join(' · ');
                    const isSettlement = item.type === 'SETTLEMENT_DAYS';
                    return (
                      <TableRow key={item.id || index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {when ? new Date(when).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {transactionLabel || '—'}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {isSettlement && (
                                // Distinguish per-merchant settlement-day events
                                // from bank-detail events at a glance.
                                <Chip
                                  size="small"
                                  label="Settlement"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 18 }}
                                />
                              )}
                              {item.action && (
                                <Chip
                                  size="small"
                                  label={item.action}
                                  color={
                                    item.action === 'CREATE'
                                      ? 'success'
                                      : 'default'
                                  }
                                  sx={{ fontSize: '0.65rem', height: 18 }}
                                />
                              )}
                              {item.merchantId && (
                                <Chip
                                  size="small"
                                  label={`Merchant: ${item.merchantId}`}
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 18,
                                    fontFamily: 'monospace',
                                  }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <ChangesCell changes={item.changes} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.updatedBy || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.remarks || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
